import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Cake, PartyPopper, X } from 'lucide-react';
import api from '../utils/api';

/**
 * Birthday / Work-Anniversary banner.
 * - If the current user is a celebrant today → show a personalised "Happy Birthday/Anniversary"
 *   message on their own screen (full-width, dismissible).
 * - For everyone else → show a soft reminder card listing today's celebrants so they can wish them.
 */
export const CelebrationsBanner = ({ currentUserId }) => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const res = await api.get('/celebrations/today');
        if (alive) setData(res.data);
      } catch (e) { /* silent — non-blocking widget */ }
    };
    fetch();
    return () => { alive = false; };
  }, []);

  if (!data || dismissed) return null;
  const myBirthday = (data.birthdays || []).find(b => b.id === currentUserId);
  const myAnniversary = (data.anniversaries || []).find(a => a.id === currentUserId);
  const othersBirthdays = (data.birthdays || []).filter(b => b.id !== currentUserId);
  const othersAnniversaries = (data.anniversaries || []).filter(a => a.id !== currentUserId);

  if (!myBirthday && !myAnniversary && othersBirthdays.length === 0 && othersAnniversaries.length === 0) {
    return null;
  }

  // Personal celebration takes priority — bold, animated banner.
  if (myBirthday || myAnniversary) {
    return (
      <Card
        className="relative mb-4 border-0 shadow-md overflow-hidden"
        data-testid="my-celebration-banner"
        style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fbbf24 100%)',
        }}
      >
        <button
          onClick={() => setDismissed(true)}
          className="absolute right-3 top-3 p-1 rounded hover:bg-black/10 transition-colors z-10"
          aria-label="Dismiss"
          data-testid="dismiss-my-celebration"
        >
          <X className="h-4 w-4 text-amber-900" />
        </button>
        <div className="px-5 py-4 flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-white/60 flex items-center justify-center flex-shrink-0">
            {myBirthday ? (
              <Cake className="h-6 w-6 text-pink-600" />
            ) : (
              <PartyPopper className="h-6 w-6 text-amber-700" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {myBirthday && (
              <p className="text-lg font-bold text-amber-900">Happy Birthday! 🎂</p>
            )}
            {myAnniversary && (
              <p className="text-lg font-bold text-amber-900">
                Happy {myAnniversary.years}-Year Work Anniversary! 🎉
              </p>
            )}
            <p className="text-sm text-amber-800">Wishing you a wonderful day from the FinTrack Pro team.</p>
          </div>
        </div>
      </Card>
    );
  }

  // Reminder to others — compact card listing today's celebrants.
  return (
    <Card className="mb-4 border-amber-200 bg-amber-50/60" data-testid="celebrations-reminder">
      <div className="px-4 py-2.5 flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <PartyPopper className="h-4 w-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900 mb-0.5">Celebrations today — take a moment to wish</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
            {othersBirthdays.map(b => (
              <span key={`b-${b.id}`} className="text-amber-800" data-testid={`celebration-bday-${b.id}`}>
                🎂 <strong>{b.full_name}</strong> (birthday)
              </span>
            ))}
            {othersAnniversaries.map(a => (
              <span key={`a-${a.id}`} className="text-amber-800" data-testid={`celebration-anniv-${a.id}`}>
                🎉 <strong>{a.full_name}</strong> ({a.years}-yr work anniversary)
              </span>
            ))}
          </div>
        </div>
        <button onClick={() => setDismissed(true)} className="p-1 rounded hover:bg-amber-100 flex-shrink-0" aria-label="Dismiss" data-testid="dismiss-celebrations">
          <X className="h-4 w-4 text-amber-700" />
        </button>
      </div>
    </Card>
  );
};
